import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Users, Calendar, DollarSign, MessageSquare, Tag } from 'lucide-react';
import { ReportMensile } from '@/components/ReportMensile';

import { AdminBookingsTab } from '@/components/admin/AdminBookingsTab';
import { AdminUsersTab } from '@/components/admin/AdminUsersTab';
import { AdminPricingTab } from '@/components/admin/AdminPricingTab';
import { AdminCategoriesTab } from '@/components/admin/AdminCategoriesTab';
import { AdminHallsTab } from '@/components/admin/AdminHallsTab';
import { AdminSettingsTab } from '@/components/admin/AdminSettingsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [halls, setHalls] = useState([]);
  const [pricing, setPricing] = useState({});
  const [categories, setCategories] = useState([]);
  const [announcement, setAnnouncement] = useState({ message: '', is_active: false });
  const [cancellationEmail, setCancellationEmail] = useState('');
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingPricing, setSavingPricing] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);
  const [savingHalls, setSavingHalls] = useState(false);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpEmail, setSmtpEmail] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, usersRes, hallsRes, pricingRes, categoriesRes, announcementRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/bookings`),
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/halls`),
        axios.get(`${API_URL}/pricing`),
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/announcement`),
        axios.get(`${API_URL}/admin-settings`)
      ]);
      setBookings(bookingsRes.data);
      setUsers(usersRes.data);
      setHalls(hallsRes.data);
      setPricing(pricingRes.data);
      setCategories(categoriesRes.data.categories || []);
      setAnnouncement(announcementRes.data);
      setCancellationEmail(settingsRes.data.cancellation_email || '');
      setTelegramBotToken(settingsRes.data.telegram_bot_token || '');
      setTelegramChatId(settingsRes.data.telegram_chat_id || '');
      setSmtpHost(settingsRes.data.smtp_host || '');
      setSmtpPort(settingsRes.data.smtp_port || 587);
      setSmtpEmail(settingsRes.data.smtp_email || '');
      setSmtpPassword(settingsRes.data.smtp_password || '');
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API_URL}/bookings/${bookingId}`);
      toast.success('Prenotazione cancellata');
      fetchData();
    } catch (error) { toast.error('Errore nella cancellazione'); }
  };

  const handleUpdatePricing = async () => {
    setSavingPricing(true);
    try {
      const { id, ...pricingData } = pricing;
      await axios.put(`${API_URL}/pricing`, pricingData);
      toast.success('Prezzi aggiornati');
      fetchData();
    } catch (error) { toast.error('Errore nell\'aggiornamento dei prezzi'); }
    finally { setSavingPricing(false); }
  };

  const handleUpdateCategories = async () => {
    setSavingCategories(true);
    try {
      await axios.put(`${API_URL}/categories`, { categories });
      toast.success('Categorie aggiornate');
      fetchData();
    } catch (error) { toast.error('Errore nell\'aggiornamento delle categorie'); }
    finally { setSavingCategories(false); }
  };

  const handleUpdateHall = async (hallId, imageUrl) => {
    setSavingHalls(true);
    try {
      await axios.put(`${API_URL}/halls/${hallId}`, { image_url: imageUrl });
      toast.success('Immagine sala aggiornata');
      fetchData();
    } catch (error) { toast.error('Errore nell\'aggiornamento dell\'immagine'); }
    finally { setSavingHalls(false); }
  };

  const handleUpdateAnnouncement = async () => {
    setSavingAnnouncement(true);
    try {
      await axios.put(`${API_URL}/announcement`, announcement);
      toast.success('Avviso aggiornato con successo');
      fetchData();
    } catch (error) { toast.error('Errore nell\'aggiornamento dell\'avviso'); }
    finally { setSavingAnnouncement(false); }
  };

  const handleUpdateSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/admin-settings`, {
        cancellation_email: cancellationEmail,
        telegram_bot_token: telegramBotToken,
        telegram_chat_id: telegramChatId,
        smtp_host: smtpHost,
        smtp_port: smtpPort,
        smtp_email: smtpEmail,
        smtp_password: smtpPassword
      });
      toast.success('Impostazioni salvate');
      fetchData();
    } catch (error) { toast.error('Errore nel salvataggio'); }
    finally { setSavingSettings(false); }
  };

  const handleSaveBookingEdit = async () => {
    if (!editingBooking) return;
    try {
      await axios.put(`${API_URL}/bookings/${editingBooking.id}`, {
        hall_id: editingBooking.hall_id,
        start_datetime: editingBooking.start_datetime,
        end_datetime: editingBooking.end_datetime,
        use_cucina: editingBooking.use_cucina,
        descrizione_manifestazione: editingBooking.descrizione_manifestazione || '',
        tipo_manifestazione: editingBooking.tipo_manifestazione,
        tipo_prezzo: editingBooking.tipo_prezzo || '3'
      });
      toast.success('Prenotazione aggiornata');
      setEditingBooking(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore aggiornamento'); }
  };

  const updateCategoryLabel = (index, newLabel) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], label: newLabel, active: newLabel.trim().length > 0 };
    setCategories(updated);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Caricamento...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <h1 className="font-heading font-extrabold text-2xl sm:text-4xl tracking-tight">Pannello Admin</h1>
          </div>
        </div>

        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="grid w-full max-w-5xl grid-cols-7">
            <TabsTrigger value="bookings" data-testid="tab-bookings"><Calendar className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Prenotazioni</span></TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Utenti</span></TabsTrigger>
            <TabsTrigger value="halls" data-testid="tab-halls"><Calendar className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Sale</span></TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories"><Tag className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Categorie</span></TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing"><DollarSign className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Prezzi</span></TabsTrigger>
            <TabsTrigger value="announcement" data-testid="tab-announcement"><MessageSquare className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Avviso</span></TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports"><DollarSign className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Report</span></TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <AdminBookingsTab bookings={bookings} halls={halls} onEdit={setEditingBooking} onDelete={handleDeleteBooking} />
          </TabsContent>

          <TabsContent value="users">
            <AdminUsersTab users={users} fetchData={fetchData} />
          </TabsContent>

          <TabsContent value="categories">
            <AdminCategoriesTab categories={categories} onUpdateLabel={updateCategoryLabel} onSave={handleUpdateCategories} saving={savingCategories} />
          </TabsContent>

          <TabsContent value="pricing">
            <AdminPricingTab pricing={pricing} setPricing={setPricing} categories={categories} onSave={handleUpdatePricing} saving={savingPricing} />
          </TabsContent>

          <TabsContent value="halls">
            <AdminHallsTab halls={halls} onUpdateHall={handleUpdateHall} saving={savingHalls} />
          </TabsContent>

          <TabsContent value="announcement">
            <AdminSettingsTab
              announcement={announcement} setAnnouncement={setAnnouncement} onSaveAnnouncement={handleUpdateAnnouncement} savingAnnouncement={savingAnnouncement}
              cancellationEmail={cancellationEmail} setCancellationEmail={setCancellationEmail}
              telegramBotToken={telegramBotToken} setTelegramBotToken={setTelegramBotToken}
              telegramChatId={telegramChatId} setTelegramChatId={setTelegramChatId}
              smtpHost={smtpHost} setSmtpHost={setSmtpHost}
              smtpPort={smtpPort} setSmtpPort={setSmtpPort}
              smtpEmail={smtpEmail} setSmtpEmail={setSmtpEmail}
              smtpPassword={smtpPassword} setSmtpPassword={setSmtpPassword}
              onSaveSettings={handleUpdateSettings} savingSettings={savingSettings}
            />
          </TabsContent>

          <TabsContent value="reports"><ReportMensile /></TabsContent>
        </Tabs>

        {/* Edit Booking Dialog */}
        <Dialog open={!!editingBooking} onOpenChange={(open) => { if (!open) setEditingBooking(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifica Prenotazione</DialogTitle>
              <DialogDescription>Modifica i dati della prenotazione. Il prezzo verr&agrave; ricalcolato.</DialogDescription>
            </DialogHeader>
            {editingBooking && (
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Data Inizio</Label>
                    <Input type="datetime-local" value={editingBooking.start_datetime?.replace(':00Z','').substring(0,16)} onChange={(e) => setEditingBooking({...editingBooking, start_datetime: e.target.value + ':00'})} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Data Fine</Label>
                    <Input type="datetime-local" value={editingBooking.end_datetime?.replace(':00Z','').substring(0,16)} onChange={(e) => setEditingBooking({...editingBooking, end_datetime: e.target.value + ':00'})} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Descrizione</Label>
                  <Input value={editingBooking.descrizione_manifestazione || ''} onChange={(e) => setEditingBooking({...editingBooking, descrizione_manifestazione: e.target.value})} />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editingBooking.use_cucina} onChange={(e) => setEditingBooking({...editingBooking, use_cucina: e.target.checked})} />
                    Cucina
                  </label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingBooking(null)}>Annulla</Button>
              <Button onClick={handleSaveBookingEdit} data-testid="confirm-edit-booking">
                <Save className="w-4 h-4 mr-2" /> Salva Modifiche
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
